import { ColDef, ValueGetterParams, ICellRendererParams, ColumnApi, GridApi } from "ag-grid-community";
import { dispatch } from "d3";
import React, { useEffect, useState } from "react";
import { LoadAllChannelOrganizations, LoadAllChannelUsers, LoadChannelUsers } from "../../../actions";
import setToast from "../../../helpers/toaster.helper";
import { EditIcon, ReInviteIcon, LockIcon, TrashIcon, AddUserIcon } from "../../../icon";
import { ChannelUserModel, ChannelUserStatus, mapToChannelUser } from "../../../models/users.model";
import { ChannelUserService } from "../../../services/ChannelUserService";
import { UtilityService } from "../../../services/UtilityService";
import ChannelInviteUser from "../../channel-users/channel-invite-user.component";
import Confirm_Dialog from "../../shared/confirm-dialog.component";
import { GridStatusLabelVariant, ActionLabels, appNote, ToasterTexts, buttonLabels, emptyLabels } from "../../shared/constants";
import GridComponent from "../../shared/grid";
import { ActionsRenderer } from "../../shared/grid/actionsRenderer";
import { dateComparator } from "../../shared/grid/grid.services";
import { StatusRenderer } from "../../shared/grid/statusRenderer";
import PageHeaderComponent from "../../shared/page-header.component";
import { ToasterType } from "../../shared/toaster/toaster.model";
import { useDispatch } from "react-redux";
import { useSelector } from 'react-redux';
import * as selector from '../../../selectors/selectors';
import { ChannelOrganizationModel } from "../../../models/channels/channels.model";
import { setHeaderTitle, setRefreshHandler } from "../../../helpers/header.helper";
import { useLocation } from "react-router";


const ChannelAllUsers = () => {

    const CurrentUser: ChannelUserModel = ChannelUserService.getCurrentUserFromLocalStorage();
    const location = useLocation();

    const dispatch = useDispatch()
    const ChannelUsers: ChannelUserModel[] = useSelector(selector.ChannelAllUsers)
    const ChannelAllOrganizations: ChannelOrganizationModel[] = useSelector(selector.ChannelAllOrganizations)

    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null)
    const [buttonName, setButtonName] = useState<string>("");
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<ChannelUserModel>();
    const [showAddUser, setShowAddUser] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [confirmDialogText, setConfirmDialogText] = useState<string>("")
    const [searchValue, setSearchValue] = useState<string>("")

    const UsersColoumnDefs: ColDef[] = [
        {
            field: "FullName",
            headerName: "Name",
        },
        {
            field: "Email",
            headerName: "Email",
            hide: false,
            cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" },
        },
        {
            field: "RoleID",
            headerName: "Role",
            valueGetter: (params: ValueGetterParams) => {
                return params.data.RoleID === 1 ? "Admin" : params.data.RoleID === 2 ? "Organization Admin" : params.data.RoleID === 3 ? "Agent" : "Manger"
            },
            hide: false,
            cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" },
        },
        {
            field: "OrganizationName",
            headerName: "Organization",
            valueGetter: (params: ValueGetterParams) => {
                return params.data.OrganizationName
            },
            hide: false,
            cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" },
        },
        {
            field: "UserStatus",
            headerName: "Status",
            hide: false,
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.data) {
                    const label = params.data.UserStatus === 1 ? "Active" : params.data.UserStatus === 2 ? "Inactive" : params.data.UserStatus === 3 ? "Pending" : "Disable";
                    const variant = params.data.UserStatus === 1 ? GridStatusLabelVariant.Active : params.data.UserStatus === 2 ? GridStatusLabelVariant.Danger : params.data.UserStatus === 3 ? GridStatusLabelVariant.Warning : GridStatusLabelVariant.Danger;
                    const Status = {
                        component: StatusRenderer,
                        params: {
                            Variant: variant, Label: label, Id: params.data.ID
                        }
                    }
                    return Status
                }
                return undefined
            },
            cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" },
        },
        {
            field: "CreatedDateTime",
            headerName: "Created Time",
            hide: false,
            valueGetter: (params: ValueGetterParams) => UtilityService.convertDateToDay(params.data.CreatedDateTime).toString(),
            cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" },
        },
        {
            field: "UpdatedDateTime",
            headerName: "Updated Time",
            valueGetter: (params: ValueGetterParams) => UtilityService.convertDateToDay(params.data.UpdatedDateTime).toString(),
            sort: 'desc',
            comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA.data.UpdatedDateTime, nodeB.data.UpdatedDateTime),
            cellStyle: { textOverflow: 'ellipsis', paddingTop: "15px" },
            suppressSizeToFit: true,
        },
        {
            field: "Action",
            maxWidth: 40,
            headerName: "",
            pinned: "right",
            lockPinned: true,
            lockPosition: "right",
            cellClass: "ag-cell-actions-style",
            headerClass: "bg-color-secondary",
            cellRenderer: params => <ActionsRenderer MenuData={getMenuActions(params.data)}
                {...params}
            />,
            resizable: false,
            sortable: false,
            hide: false
        },
    ]
    const getMenuActions = (data: ChannelUserModel) => {
        return [
            {
                Name: ActionLabels.EDIT,
                Icon: EditIcon,
                OnClick: () => openAdduser(data)
            },
            {
                Name: ActionLabels.REINVITE,
                Icon: ReInviteIcon,
                OnClick: () => openConfirmDialog(ActionLabels.REINVITE, appNote.RE_INVITE_USER, data),
                Hide: data.UserStatus !== 3
            },
            {
                Name: data?.UserStatus === 1 ? ActionLabels.DISABLE : ActionLabels.ENABLE,
                Icon: LockIcon,
                OnClick: () => openConfirmDialog(data.UserStatus === 1 ? ActionLabels.DISABLE : ActionLabels.ENABLE, data?.UserStatus === 5 ? appNote.ENABLE_USER : appNote.DISABLE_USER, data),
                Hide: (CurrentUser.ID === data.ID || data.UserStatus === ChannelUserStatus.INACTIVE || data.UserStatus === ChannelUserStatus.PENDING)
            },
            {
                Name: ActionLabels.DELETE,
                Icon: TrashIcon,
                OnClick: () => openConfirmDialog(ActionLabels.DELETE, appNote.DELETE_USER, data),
                Hide: CurrentUser.ID === data.ID
            }
        ]
    }

    const openAdduser = (data?: ChannelUserModel) => {
        setShowAddUser(true)
        setSelectedUser(data)
    }
    const closeAdduser = () => {
        setShowAddUser(false)
        setSelectedUser(undefined)
    }

    const openConfirmDialog = (buttonname: string, text: string, data: ChannelUserModel) => {
        setButtonName(buttonname);
        setShowConfirmDialog(true);
        setSelectedUser(data);
        setConfirmDialogText(text);
    }

    const closeConfirmDialog = (confirm?: boolean) => {
        if (confirm) {
            setIsLoading(true)
            if (buttonName === ActionLabels.REINVITE) {
                const obj = {
                    "fName": selectedUser.FirstName,
                    "lName": selectedUser.LastName,
                    "email": selectedUser.Email.trim(),
                    "role": selectedUser.RoleID,
                    "admin_id": ChannelUserService.getCurrentUserFromLocalStorage()?.ID,
                }
                ChannelUserService.sendInvitation(obj)
                    .then(() => {
                        setIsLoading(false)
                        setToast(ToasterTexts.REINVITE_MESSAGE, ToasterType.SUCCESS)
                        resetState()
                    })
                    .catch((err) => {
                        setToast(err.response.data.message, ToasterType.DANGER)
                        resetState()
                        setIsLoading(false)
                    });

            }
            else if (buttonName === ActionLabels.DELETE) {
                ChannelUserService.deleteUser(selectedUser.ID, CurrentUser.ID).then((res) => {
                    resetState()
                    LoadAllChannelUsers(dispatch)
                    setToast(ToasterTexts.USER_EDIT_SUCCESS.replace('<Name>', selectedUser.FullName), ToasterType.SUCCESS)
                }).catch((err) => {
                    resetState()
                })
            }
            else {
                ChannelUserService.disableUser(selectedUser.ID).then((res) => {
                    resetState()
                    LoadAllChannelUsers(dispatch)
                    setToast((selectedUser.UserStatus === 1 ? ToasterTexts.USER_DISABLE_SUCCESS : ToasterTexts.USER_ENABLE_SUCCESS).replace('<Name>', selectedUser.FullName), ToasterType.SUCCESS)
                }).catch((err) => {
                    resetState()
                })
            }
        }
        else {
            resetState();
        }
    }

    const resetState = () => {
        setButtonName("");
        setShowConfirmDialog(false);
        setSelectedUser(undefined);
        setIsLoading(false)
        setConfirmDialogText("")
    }

    const getGridTitle = () => {
        return ChannelUsers?.length === 1 ? "1 User" : !!ChannelUsers?.length && ChannelUsers?.length + " Users"
    }

    const searchUser = (e: any) => {
        setSearchValue(e.target.value)
        gridApi.setQuickFilter(e.target.value);
    }

    const onGridReady = (params) => {
        setGridApi(params.api);
        setColumnApi(params.columnApi)
    }

    useEffect(() => {
        LoadAllChannelUsers(dispatch)
        LoadAllChannelOrganizations(dispatch)
        setHeaderTitle("ChannelAllUsers")
        setRefreshHandler(() => refreshData())
    }, [])

    const refreshData = () => {
        if (!!gridApi) {
            gridApi.refreshCells({ force: true })
            gridApi.deselectAll();
        }
        LoadAllChannelUsers(dispatch)
    }

    useEffect(() => {
        const state = location.state as any;
        const organizationName = state?.OrganizationName
        if (!!gridApi && !!organizationName) {
            gridApi.setQuickFilter(organizationName)
            setSearchValue(organizationName)
        }
    }, [gridApi])

    return (
        <div className="d-flex flex-direction h-p-100 o-hidden justify-between">
            <div >
                <PageHeaderComponent
                    GridTitle={getGridTitle()}
                    onSearch={searchUser}
                    Count={10}
                    searchValue={searchValue}
                    PlaceHolder={"Find a user ..."}
                    Actions={[{
                        OnClick: () => openAdduser(),
                        ButtonLabel: buttonLabels.ADD_USER,
                        Icon: AddUserIcon,
                        Variant: "primary-btn",

                    }]}
                />
            </div>
            <GridComponent
                ColumnDefs={UsersColoumnDefs}
                RowData={ChannelUsers?.filter(x => x.UserStatus !== ChannelUserStatus.DELETED)}
                OnGridReady={onGridReady}
                IsPaginationEnabled={true}
                EmptyLabel={emptyLabels.USERS}
                IsFilterPresent={true}
                gridApi={gridApi}
                LockVisible={false}
                filterOptions={[]}
            />

            {showConfirmDialog &&
                <Confirm_Dialog
                    ButtonName={buttonName}
                    onClose={closeConfirmDialog}
                    Show={showConfirmDialog}
                    Text={confirmDialogText}
                    Loading={isLoading}
                />
            }

            {showAddUser &&
                <ChannelInviteUser
                    show={showAddUser}
                    onClose={() => closeAdduser()}
                    User={selectedUser}
                    isSysAdmin={true}
                    Organizations={ChannelAllOrganizations}
                />
            }
        </div>
    )
}

export default ChannelAllUsers;